import {
	DEFAULT_CONFS,
	DEFAULT_DIVS,
	DEFAULT_POINTS_FORMULA,
	DEFAULT_STADIUM_CAPACITY,
} from "./constants.ts";
import { bySport, isSport } from "./sportFunctions.ts";
import {
	type AwardSettingIndividual,
	type AwardSettingTeam,
	type GameAttributeKey,
	type GameAttributesLeagueWithHistory,
	type GameAttributeWithHistory,
} from "./types.ts";

export const wrapFromStart = <T>(value: T): GameAttributeWithHistory<T> => [
	{
		start: -Infinity,
		value,
	},
];

// gameAttributes is mixed up between league settings, game state, teams, and cache
export const gameAttributesKeysGameState: GameAttributeKey[] = [
	"phase",
	"nextPhase",
	"gameOver",
	"godMode",
	"godModeInPast",
	"otherTeamsWantToHire",
	"lowestDifficulty",
	"difficulty",
	"gracePeriodEnd",
	"lid",
	"userTid",
	"userTids",
	"season",
	"startingSeason",
	"numDraftPicksCurrent",
	"expansionDraft",
	"autoRelocate",
	"autoExpand",
	"tradeProposalsSeed",
	"tradeProposalsAvailable",
	"tradeProposalsAvailableDpids",
	"tradeProposalsLookingFor",
	"tradeProposalsOffered",
	"topProspects",
];
export const gameAttributesKeysTeams: GameAttributeKey[] = ["confs", "divs"];
export const gameAttributesCache: GameAttributeKey[] = [
	"numTeams",
	"numActiveTeams",
	"teamInfoCache",
];

const gameAttributesKeysSportSpecific = {
	baseball: new Set<GameAttributeKey>([
		"dh",
		"foulFactor",
		"groundFactor",
		"lineFactor",
		"flyFactor",
		"powerFactor",
		"stealFactor",
		"throwOutFactor",
		"strikeFactor",
		"balkFactor",
		"wildPitchFactor",
		"passedBallFactor",
		"hitByPitchFactor",
		"swingFactor",
		"contactFactor",
		"hitFactor",
		"errorFactor",
	]),
	basketball: new Set<GameAttributeKey>([
		"threePointers",
		"threePointTendencyFactor",
		"threePointAccuracyFactor",
		"twoPointAccuracyFactor",
		"ftAccuracyFactor",
		"blockFactor",
		"stealFactor",
		"turnoverFactor",
		"orbFactor",
		"assistFactor",
		"realPlayerDeterminism",
		"rpdPot",
		"foulRateFactor",
		"foulsNeededToFoulOut",
		"foulsUntilBonus",
		"elam",
		"elamASG",
		"elamMinutes",
		"elamOvertime",
		"elamPoints",
		"randomDebutsForever",
		"realDraftRatings",
		"numPlayersDunk",
		"numPlayersThree",
		"quarterLength",
		"overtimeLength",
		"overtimeLengthPlayoffs",
		"numPlayersOnCourt",
		"pace",
		"allStarDunk",
		"allStarThree",
		"forceRetireRealPlayers",
		"forceHistoricalRosters",
		"easterEggPlayers",
	]),
	football: new Set<GameAttributeKey>([
		"fantasyPoints",
		"foulRateFactor",
		"quarterLength",
		"overtimeLength",
		"overtimeLengthPlayoffs",
		"passFactor",
		"rushYdsFactor",
		"passYdsFactor",
		"completionFactor",
		"scrambleFactor",
		"sackFactor",
		"fumbleFactor",
		"intFactor",
		"fgAccuracyFactor",
		"fourthDownFactor",
		"onsideFactor",
		"onsideRecoveryFactor",
		"pace",
		"scrimmageTouchbackKickoff",
		"twoPointConversions",
		"footballOvertime",
		"footballOvertimePlayoffs",
	]),
	hockey: new Set<GameAttributeKey>([
		"foulRateFactor",
		"quarterLength",
		"overtimeLength",
		"overtimeLengthPlayoffs",
		"hitFactor",
		"giveawayFactor",
		"takeawayFactor",
		"blockFactor",
		"deflectionFactor",
		"saveFactor",
		"assistFactor",
		"pace",
	]),
};
export const gameAttributesKeysOtherSports = new Set<GameAttributeKey>();
for (const [sport, keys] of Object.entries(gameAttributesKeysSportSpecific)) {
	if (sport !== process.env.SPORT) {
		for (const key of keys) {
			if (!gameAttributesKeysSportSpecific[process.env.SPORT].has(key)) {
				gameAttributesKeysOtherSports.add(key);
			}
		}
	}
}

// Define awards up here mostly for achievements, we need to know what the default awards are for some categories across sports
export const defaultAwards = {
	all: bySport<AwardSettingTeam>({
		baseball: {
			shortName: "OFF",
			name: "Silver Slugger",
			formula: "rbr + rbat",
			showStats: "offense",
			group: "conf",
			numTeams: 1,
		},
		basketball: {
			shortName: "ALL",
			name: "All-League",
			formula:
				"ewa / 22 + vorp / 32 + min(ws/max(teamWs,ws),0.8) / 10 + seasonFraction * winp",
			showStats: "offense",
			numTeams: 3,
		},
		football: {
			shortName: "ALL",
			name: "All-League",
			formula:
				"4*defSk+0.4*defTckLoss+0.2*defTckAst+0.4*defTckSolo+3*defFmbFrc+3*defFmbRec+6*defInt+2*defPssDef",
			formulaByPos: {
				QB: "0.5*(0.125*rusYds+6*rusTD-2*fmbLost) + 1.1*(0.04*pssYds+4*pssTD-2.5*pssInt)",
				RB: "0.125*rusYds+6*rusTD-2*fmbLost + 0.5*(0.0975*recYds+6*recTD)",
				WR: "0.0975*recYds+6*recTD",
				TE: "0.0975*recYds+6*recTD",
				OL: "(pbw+rbw)/(0.1+pba+rba)*(pba+rba)^(1/2)",
				K: "fg",
				P: "pntYds",
			},
			showStats: "overall",
			numTeams: 2,
		},
		hockey: {
			shortName: "ALL",
			name: "All-League",
			showStats: "overall",
			formula: "ps",
			numTeams: 2,
		},
	}),
	alr: bySport<AwardSettingTeam>({
		baseball: {
			shortName: "ALR",
			name: "All-Rookie",
			formula: "war",
			showStats: "overall",
			group: "conf",
			rookie: true,
			numTeams: 1,
		},
		basketball: {
			shortName: "ALR",
			name: "All-Rookie",
			formula: "ewa / 2.1 + vorp + gp / 82 * pts / 2",
			showStats: "offense",
			rookie: true,
			numTeams: 2,
		},
		football: {
			shortName: "ALR",
			name: "All-Rookie",
			formula:
				"4*defSk+0.4*defTckLoss+0.2*defTckAst+0.4*defTckSolo+3*defFmbFrc+3*defFmbRec+6*defInt+2*defPssDef",
			formulaByPos: {
				QB: "0.5*(0.125*rusYds+6*rusTD-2*fmbLost) + 1.1*(0.04*pssYds+4*pssTD-2.5*pssInt)",
				RB: "0.125*rusYds+6*rusTD-2*fmbLost + 0.5*(0.0975*recYds+6*recTD)",
				WR: "0.0975*recYds+6*recTD",
				TE: "0.0975*recYds+6*recTD",
				OL: "(pbw+rbw)/(0.1+pba+rba)*(pba+rba)^(1/2)",
			},
			showStats: "overall",
			rookie: true,
			numTeams: 1,
		},
		hockey: {
			shortName: "ALR",
			name: "All-Rookie",
			showStats: "overall",
			formula: "ps",
			rookie: true,
			numTeams: 1,
		},
	}),
	fmvp: bySport<AwardSettingIndividual>({
		baseball: {
			shortName: "FMVP",
			name: "World Series MVP",

			// This formula was called fakeWAR in the old award system https://github.com/zengm-games/zengm/blob/4ee432c5b9097ed978749a049fff5823711690dc/src/worker/core/season/doAwards.baseball.ts#L285-L302 only difference now is that some values (abf and totalERA) are estimates from a typical ZGMB league rather than computed from the series total stats. Which arguably is better since there is a lot of noise in an individual series.
			formula:
				"((0.47*h + 0.38*2b + 0.55*3b + 0.93*hr + 0.33*(bb+hbp) - 0.276*(pa-bb-hbp-sf-h)) + (0.3*sb - 0.6*cs) + ((outs/27)*4.04 - er)) * (1+0.75*won)",
			showStats: "overall",
			statRange: -1,
		},
		basketball: {
			shortName: "FMVP",
			name: "Finals MVP",
			formula: "gmsc * (1 + 0.75*won)",
			showStats: "offense",
			statRange: -1,
		},
		football: {
			shortName: "FMVP",
			name: "Finals MVP",
			formula:
				"(pssYds/25 + 4*pssTD + (recYds+rusYds)/10 + 6*(recTD+rusTD+prTD+krTD+defIntTD+defFmbTD) + 1.75*(1.75*defSk + (defTckSolo + defTckAst)/10 + 2*defInt + defPssDef + 2*defFmbFrc + 2*defFmbRec + 5*defSft)) * (1+0.5*won)",
			showStats: "overall",
			statRange: -1,
		},
		hockey: {
			shortName: "PMVP",
			name: "Playoff MVP",
			formula: "pts / 25 + ps - 0.225 * gps + 20 * seasonFraction * winp",
			showStats: "overall",
			statRange: "playoffs",
		},
	}),
	mvp: bySport<AwardSettingIndividual>({
		baseball: {
			shortName: "MVP",
			name: "Most Valuable Player",
			formula: "war",
			showStats: "overall",
			group: "conf",
			actAs: "mvp",
		},
		basketball: {
			shortName: "MVP",
			name: "Most Valuable Player",

			// Max is to handle negative teamWs. Min is to handle the case that a team has very low WS, don't let anybody have a crazy high fracWS
			formula:
				"ewa / 22 + vorp / 32 + min(ws/max(teamWs,ws),0.8) / 10 + seasonFraction * winp",
			showStats: "offense",
			actAs: "mvp",
		},
		football: {
			shortName: "MVP",
			name: "Most Valuable Player",
			formula:
				"0.125*rusYds+6*rusTD-2*fmbLost + 0.0975*recYds+6*recTD + 1.1*(0.04*pssYds+4*pssTD-2.5*pssInt) + 2.25*(4*defSk+0.4*defTckLoss+0.2*defTckAst+0.4*defTckSolo+3*defFmbFrc+3*defFmbRec+6*defInt+2*defPssDef) + 6*(prTD+krTD) + 4*(pbw+rbw)/(0.1+pba+rba)*(pba+rba)^(1/2)",
			formulaByPos: {
				QB: "0.5*(0.125*rusYds+6*rusTD-2*fmbLost) + 0.0975*recYds+6*recTD + 1.1*(0.04*pssYds+4*pssTD-2.5*pssInt) + 2.25*(4*defSk+0.4*defTckLoss+0.2*defTckAst+0.4*defTckSolo+3*defFmbFrc+3*defFmbRec+6*defInt+2*defPssDef) + 6*(prTD+krTD) + 4*(pbw+rbw)/(0.1+pba+rba)*(pba+rba)^(1/2)",
			},
			showStats: "overall",
			actAs: "mvp",
		},
		hockey: {
			shortName: "MVP",
			name: "Most Valuable Player",
			formula: "pts / 25 + ps - 0.225 * gps + 20 * seasonFraction * winp",
			showStats: "overall",
			actAs: "mvp",
		},
	}),
};

export const defaultAwardsBaseball = {
	def: {
		shortName: "DEF",
		name: "Gold Glove",
		formula: "rfld",
		showStats: "defense",
		group: "conf",
		numTeams: 1,
	} as AwardSettingTeam,
	poy: {
		shortName: "POY",
		name: "Cy Young Award",
		formula: "rpit",
		showStats: "sp",
		group: "conf",
	} as AwardSettingIndividual,
	roy: {
		shortName: "ROY",
		name: "Rookie of the Year",
		formula: "war",
		showStats: "overall",
		group: "conf",
		rookie: true,
		actAs: "roy",
	} as AwardSettingIndividual,
	rpoy: {
		shortName: "RPOY",
		name: "Reliever of the Year",
		formula: "rpit",
		showStats: "rp",
		group: "conf",
		bench: true,
	} as AwardSettingIndividual,
	// Best hitter in each league
	hankAaron: {
		shortName: "HAA",
		name: "Hank Aaron Award",
		formula: "rbat + rbr",
		showStats: "offense",
		group: "conf",
	} as AwardSettingIndividual,
	// Best players in all of baseball, first and second team
	allMlb: {
		shortName: "AMLB",
		name: "All-MLB Team",
		formula: "war",
		showStats: "overall",
		numTeams: 2,
	} as AwardSettingTeam,
};

export const defaultAwardsBasketball = {
	def: {
		shortName: "DEF",
		name: "All-Defensive",
		formula:
			"dws/3.1 + seasonFraction * winp + gp / 82 * (blk / 4.1 + stl / 1.8)",
		showStats: "defense",
		numTeams: 2,
	} as AwardSettingTeam,
	dpoy: {
		shortName: "DPOY",
		name: "Defensive Player of the Year",
		formula:
			"dws/3.1 + seasonFraction * winp + gp / 82 * (blk / 4.1 + stl / 1.8)",
		showStats: "defense",
	} as AwardSettingIndividual,
	mip: {
		shortName: "MIP",
		name: "Most Improved Player",
		formula: "pts + trb + ast + per",
		showStats: "offense",
		mip: true,
	} as AwardSettingIndividual,
	roy: {
		shortName: "ROY",
		name: "Rookie of the Year",
		formula: "ewa / 2.1 + vorp + gp / 82 * pts / 2",
		showStats: "offense",
		rookie: true,
		actAs: "roy",
	} as AwardSettingIndividual,
	smoy: {
		shortName: "SMOY",
		name: "Sixth Man of the Year",
		formula:
			"ewa / 5.5 + vorp / 2.3 + ws / 4.9 + seasonFraction * winp + gp / 82 * pts / 9.9",
		showStats: "offense",
		bench: true,
	} as AwardSettingIndividual,

	sfmvp: {
		shortName: "SFMVP",
		name: "Semifinals MVP",
		formula: "gmsc * (1 + 0.75*won)",
		showStats: "offense",
		statRange: -2,
	} as AwardSettingIndividual,
};

export const defaultAwardsFootball = {
	dpoy: {
		shortName: "DPOY",
		name: "Defensive Player of the Year",
		formula:
			"4*defSk+0.4*defTckLoss+0.2*defTckAst+0.4*defTckSolo+3*defFmbFrc+3*defFmbRec+6*defInt+2*defPssDef",
		showStats: "defense",
	} as AwardSettingIndividual,
	droy: {
		shortName: "DROY",
		name: "Defensive Rookie of the Year",
		formula:
			"4*defSk+0.4*defTckLoss+0.2*defTckAst+0.4*defTckSolo+3*defFmbFrc+3*defFmbRec+6*defInt+2*defPssDef",
		showStats: "defense",
		rookie: true,
		actAs: "roy",
	} as AwardSettingIndividual,
	opoy: {
		shortName: "OPOY",
		name: "Offensive Player of the Year",
		formula: "0.125*rusYds+6*rusTD-2*fmbLost + 0.0975*recYds+6*recTD",
		showStats: "overall",
		opoyFormula:
			"0.125*rusYds+6*rusTD-2*fmbLost + 0.0975*recYds+6*recTD + 1.1*(0.04*pssYds+4*pssTD-2.5*pssInt)",
	} as AwardSettingIndividual,
	oroy: {
		shortName: "OROY",
		name: "Offensive Rookie of the Year",
		formula:
			"0.125*rusYds+6*rusTD-2*fmbLost + 0.0975*recYds+6*recTD + 1.1*(0.04*pssYds+4*pssTD-2.5*pssInt)",
		formulaByPos: {
			QB: "0.5*(0.125*rusYds+6*rusTD-2*fmbLost) + 0.0975*recYds+6*recTD + 1.1*(0.04*pssYds+4*pssTD-2.5*pssInt)",
		},
		showStats: "overall",
		rookie: true,
		actAs: "roy",
	} as AwardSettingIndividual,
	poy: {
		shortName: "POY",
		name: "Protector of the Year",
		formula: "(pbw+rbw)/(0.1+pba+rba)*(pba+rba)^(1/2)",
		showStats: "blocking",
	} as AwardSettingIndividual,
};

export const defaultAwardsHockey = {
	dfoy: {
		shortName: "DFOY",
		name: "Defensive Forward of the Year",
		formula: "tk / 25 + hit / 25 + dps",
		formulaByPos: {
			D: "0",
			G: "0",
		},
		showStats: "defense",
	} as AwardSettingIndividual,
	dpoy: {
		shortName: "DPOY",
		name: "Defensive Player of the Year",
		formula: "tk / 25 + hit / 25 + dps",
		showStats: "defense",
	} as AwardSettingIndividual,
	goy: {
		shortName: "GOY",
		name: "Goalie of the Year",

		// Originally wrote this as "(gps / gpGoalie) * min(0.75 * teamGp, gpGoalie)" but that behaves poorly when gpGoalie is 0
		formula: "gps * min(0.75 * teamGp / gpGoalie, 1)",
		showStats: "goalkeeping",
	} as AwardSettingIndividual,
	roy: {
		shortName: "ROY",
		name: "Rookie of the Year",
		formula: "pts / 25 + ps - 0.225 * gps",
		showStats: "overall",
		rookie: true,
		actAs: "roy",
	} as AwardSettingIndividual,
};

export const defaultGameAttributes: GameAttributesLeagueWithHistory = {
	topProspects: undefined,
	phase: 0,
	nextPhase: undefined, // Used only for fantasy draft
	playerBioInfo: undefined,
	injuries: undefined,
	tragicDeaths: undefined,
	daysLeft: 0, // Used only for free agency
	gameOver: false,
	godMode: false,
	godModeInPast: false,
	salaryCap: 150000, // [thousands of dollars]
	minPayroll: 95000, // [thousands of dollars]
	luxuryPayroll: 168000, // [thousands of dollars]
	luxuryTax: 1.5,
	minContract: 1200, // [thousands of dollars]
	maxContract: 50000, // [thousands of dollars]
	minContractLength: 1,
	maxContractLength: 5,
	minRosterSize: 10,
	// Baseball: 20 = at least 11 position players and 9 pitchers (scales with the setting); see
	// minorsComposition.baseball.ts and fillMinorsMinimum in manageRoster.baseball.ts
	minMinorsSize: 20,
	// Baseball: a starter comes out once he reaches this pitch count (GameSim.baseball)
	starterPitchLimit: 100,
	// Baseball: most players an organization keeps in the minors (minorsLifecycle.baseball.ts); 0 = no limit
	maxMinorsSize: 30,
	maxRosterSize: 15,
	softCapTradeSalaryMatch: 125,
	numGames: wrapFromStart(82), // per season
	numGamesDiv: 16,
	numGamesConf: 36,
	otherTeamsWantToHire: false,
	numPeriods: 4, // per game
	quarterLength: 12, // [minutes]
	overtimeLength: 5,
	overtimeLengthPlayoffs: null,
	confs: wrapFromStart(DEFAULT_CONFS),
	divs: wrapFromStart(DEFAULT_DIVS),
	numGamesPlayoffSeries: wrapFromStart([7, 7, 7, 7]),
	numPlayoffByes: wrapFromStart(0),
	aiTradesFactor: 1,
	stopOnInjury: false,
	stopOnInjuryGames: 20,
	// According to data/injuries.ods, 0.25 injuries occur every game. Divided over 10 players and ~200 possessions, that means each player on the court has P = 0.25 / 10 / 200 = 0.000125 probability of being injured this play.
	injuryRate: 0.25 / 10 / 200,
	homeCourtAdvantage: 1,
	// The tragic death rate is the probability that a player will die a tragic death on a given regular season day. Yes, this only happens in the regular season. With roughly 100 days in a season, the default is about one death every 50 years.
	tragicDeathRate: 1 / (100 * 50),
	// The probability that a new player will be the son or brother of an existing player. In practice, the observed number may be smaller than this because sometimes a valid match will not be found.
	sonRate: 0.02,
	brotherRate: 0.02,
	forceRetireAge: 0,
	forceRetireSeasons: 0,
	minRetireAge: 26,
	groupScheduleSeries: false,

	salaryCapType: "soft",

	maxOvertimes: wrapFromStart(null),
	shootoutRounds: wrapFromStart(0),
	maxOvertimesPlayoffs: null,
	shootoutRoundsPlayoffs: 0,

	otl: wrapFromStart(false),

	draftType: "nba2027",
	draftLotteryCustomChances: [
		140, 140, 140, 125, 105, 90, 75, 60, 45, 30, 20, 15, 10, 5,
	],
	draftLotteryCustomNumPicks: 4,
	numDraftRounds: 2,
	draftAges: [19, 22],
	defaultStadiumCapacity: DEFAULT_STADIUM_CAPACITY,
	playersRefuseToNegotiate: true,
	allStarGame: 0.7,
	allStarNum: 12,
	allStarType: "byConf",
	allStarDunk: true,
	allStarThree: true,
	budget: true,
	numSeasonsFutureDraftPicks: 4,
	foulRateFactor: 1,
	foulsNeededToFoulOut: 6,
	foulsUntilBonus: [5, 4, 2],
	rookieContractLengths: [3, 2],
	rookiesCanRefuse: true,

	pace: 100,
	threePointers: true,
	threePointTendencyFactor: 1,
	threePointAccuracyFactor: 1,
	twoPointAccuracyFactor: 1,
	ftAccuracyFactor: 1,
	blockFactor: 1,
	stealFactor: 1,
	turnoverFactor: 1,
	orbFactor: 1,
	expansionDraft: { phase: "setup" },

	challengeNoDraftPicks: false,
	challengeNoFreeAgents: false,
	challengeNoRatings: false,
	challengeNoTrades: false,
	challengeLoseBestPlayer: false,
	challengeFiredLuxuryTax: false,
	challengeFiredMissPlayoffs: false,
	challengeSisyphusMode: false,
	challengeThanosMode: 0,
	thanosCooldownEnd: undefined,
	repeatSeason: undefined,
	equalizeRegions: false,
	realPlayerDeterminism: 0,
	spectator: false,
	elam: false,
	elamASG: false,
	elamMinutes: 4,
	elamOvertime: false,
	elamPoints: 8,
	playerMoodTraits: true,
	numPlayersOnCourt: 5,
	aiJerseyRetirement: true,
	tiebreakers: wrapFromStart([
		"headToHeadRecord",
		"divWinner",
		"divRecordIfSame",
		"confRecordIfSame",
		"marginOfVictory",
		"coinFlip",
	]),
	hofFactor: 1,
	tradeDeadline: 0.6,
	pointsFormula: wrapFromStart(""),
	randomDebutsForever: undefined,
	realDraftRatings: undefined,
	hideDisabledTeams: false,
	goatFormula: undefined,
	goatSeasonFormula: undefined,
	inflationAvg: 0,
	inflationMax: 0,
	inflationMin: 0,
	inflationStd: 0,
	riggedLottery: undefined,
	numDraftPicksCurrent: undefined,
	playoffsByConf: true,
	playoffsNumTeamsDiv: wrapFromStart(0),
	playoffsReseed: false,
	playIn: true,
	numPlayersDunk: 4,
	numPlayersThree: 8,
	draftPickAutoContract: true,
	draftPickAutoContractPercent: 25,
	draftPickAutoContractRounds: 1,
	dh: "all",
	gender: "male",
	heightFactor: 1,
	weightFactor: 1,
	numWatchColors: 1,
	autoExpand: undefined,
	autoExpandProb: 0,
	autoExpandNumTeams: 2,
	autoExpandMaxNumTeams: 40,
	autoExpandGeo: "naFirst",
	autoRelocate: undefined,
	autoRelocateProb: 0,
	autoRelocateGeo: "naFirst",
	autoRelocateRebrand: true,
	autoRelocateRealign: true,
	alwaysShowCountry: false,

	// These will always be overwritten when creating a league, just here for TypeScript
	lid: 0,
	userTid: wrapFromStart(0),
	userTids: [0],
	season: 0,
	startingSeason: 0,
	teamInfoCache: [],
	gracePeriodEnd: 0,
	numTeams: 0,
	numActiveTeams: 0,
	difficulty: 0, // See constants.DIFFICULTY for values
	lowestDifficulty: 0,
	fantasyPoints: undefined,

	// These are only for FBGM, but for TypeScript define them here
	passFactor: 1,
	rushYdsFactor: 1,
	passYdsFactor: 1,
	completionFactor: 1,
	scrambleFactor: 1,
	sackFactor: 1,
	fumbleFactor: 1,
	intFactor: 1,
	fgAccuracyFactor: 1,
	fourthDownFactor: 1,
	onsideFactor: 1,
	onsideRecoveryFactor: 1,
	scrimmageTouchbackKickoff: 35,
	twoPointConversions: true,
	footballOvertime: "bothPossess",
	footballOvertimePlayoffs: "bothPossess",

	// These are only for ZGMH, but for TypeScript define them here
	hitFactor: 1,
	giveawayFactor: 1,
	takeawayFactor: 1,
	deflectionFactor: 1,
	saveFactor: 1,
	assistFactor: 1,

	// These are only for ZGMB, but for TypeScript define them here
	foulFactor: 1,
	groundFactor: 1,
	lineFactor: 1,
	flyFactor: 1,
	powerFactor: 1,
	throwOutFactor: 1,
	strikeFactor: 1,
	balkFactor: 1,
	wildPitchFactor: 1,
	passedBallFactor: 1,
	hitByPitchFactor: 1,
	swingFactor: 1,
	contactFactor: 1,
	errorFactor: 1,

	neutralSite: "never",
	tradeProposalsSeed: 0,
	tradeProposalsAvailable: {},
	tradeProposalsAvailableDpids: {},
	tradeProposalsLookingFor: {},
	tradeProposalsOffered: {},
	rpdPot: true,
	saveOldBoxScores: {
		pastSeasons: 2,
		pastSeasonsType: "all",
		note: "all",
	},
	currencyFormat: ["$", ".", ""],
	forceRetireRealPlayers: false,
	forceHistoricalRosters: false,
	easterEggPlayers: true,
	fakeAgeProb: 0.01,
	awards: [
		defaultAwards.mvp,
		defaultAwardsBasketball.dpoy,
		defaultAwardsBasketball.roy,
		defaultAwardsBasketball.smoy,
		defaultAwardsBasketball.mip,
		defaultAwards.fmvp,
		defaultAwardsBasketball.sfmvp,
		defaultAwards.all,
		defaultAwardsBasketball.def,
		defaultAwards.alr,
	],
};

// Extra condition for NODE_ENV is because we use this export only in tests, so we don't want it in the basketball bundle!
export const footballOverrides: Partial<GameAttributesLeagueWithHistory> =
	process.env.NODE_ENV === "test" || isSport("football")
		? {
				numGames: wrapFromStart(17),
				numGamesDiv: 6,
				numGamesConf: 6,
				quarterLength: 15,
				overtimeLength: 10,
				overtimeLengthPlayoffs: 15,
				numGamesPlayoffSeries: wrapFromStart([1, 1, 1, 1]),
				numPlayoffByes: wrapFromStart(2),
				stopOnInjuryGames: 1,
				salaryCapType: "hard",
				maxOvertimes: wrapFromStart(1),
				draftType: "noLottery",
				numDraftRounds: 7,
				draftAges: [21, 22],
				salaryCap: 200000,
				minPayroll: 150000,
				luxuryPayroll: 250000,
				minContract: 500,
				maxContract: 30000,
				minRosterSize: 40,
				maxRosterSize: 55,
				// Arbitrary - 2 injuries per game. Divide over 1000 plays
				injuryRate: 2 / 1000,
				// The tragic death rate is the probability that a player will die a tragic death on a given regular season day. Yes, this only happens in the regular season. With roughly 20 days in a season, the default is about one death every 50 years.
				tragicDeathRate: 1 / (20 * 50),
				sonRate: 0.005,
				brotherRate: 0.005,
				allStarGame: -1,
				allStarNum: 44,
				allStarType: "byConf",
				numPlayersOnCourt: 11,
				tiebreakers: wrapFromStart([
					"headToHeadRecord",
					"divRecordIfSame",
					"commonOpponentsRecord",
					"confRecordIfSame",
					"strengthOfVictory",
					"strengthOfSchedule",
					"marginOfVictory",
					"coinFlip",
				]),
				tradeDeadline: 0.5,
				playoffsReseed: true,
				playoffsNumTeamsDiv: wrapFromStart(1),
				playIn: false,
				fantasyPoints: "standard",
				draftPickAutoContract: false,
				pace: 1,
				hofFactor: 1.2,
				neutralSite: "finals",
				awards: [
					defaultAwards.mvp,
					defaultAwardsFootball.opoy,
					defaultAwardsFootball.poy,
					defaultAwardsFootball.dpoy,
					defaultAwardsFootball.oroy,
					defaultAwardsFootball.droy,
					defaultAwards.fmvp,
					defaultAwards.all,
					defaultAwards.alr,
				],
			}
		: {};

export const hockeyOverrides: Partial<GameAttributesLeagueWithHistory> =
	process.env.NODE_ENV === "test" || isSport("hockey")
		? {
				numGames: wrapFromStart(84),
				numGamesDiv: 28,
				numGamesConf: 24,
				quarterLength: 20,
				overtimeLength: 5,
				overtimeLengthPlayoffs: 20,
				numPeriods: 3,
				salaryCapType: "hard",
				salaryCap: 105000,
				minPayroll: 60000,
				luxuryPayroll: 120000,
				minContract: 700,
				maxContract: 20000,
				minRosterSize: 24,
				maxRosterSize: 26,
				// Injury rate per player per possession, basically. But it's a little more complicated than that.
				injuryRate: 1 / 10000,
				draftType: "nhl2021",
				numDraftRounds: 4,
				draftAges: [18, 21],
				allStarNum: 20,
				allStarType: "byConf",
				numPlayersOnCourt: 6,
				otl: wrapFromStart(true),
				pointsFormula: wrapFromStart(DEFAULT_POINTS_FORMULA),
				playoffsNumTeamsDiv: wrapFromStart(3),
				playIn: false,
				draftPickAutoContractPercent: 10,
				draftPickAutoContractRounds: 2,
				rookieContractLengths: [3],
				pace: 1,
				maxOvertimes: wrapFromStart(1),
				shootoutRounds: wrapFromStart(3),
				awards: [
					defaultAwards.mvp,
					defaultAwardsHockey.goy,
					defaultAwardsHockey.dpoy,
					defaultAwardsHockey.dfoy,
					defaultAwardsHockey.roy,
					defaultAwards.fmvp,
					defaultAwards.all,
					defaultAwards.alr,
				],
			}
		: {};

// Extra condition for NODE_ENV is because we use this export only in tests, so we don't want it in the basketball bundle!
export const baseballOverrides: Partial<GameAttributesLeagueWithHistory> =
	process.env.NODE_ENV === "test" || isSport("baseball")
		? {
				numGames: wrapFromStart(162),
				numGamesDiv: 76,
				numGamesConf: null,
				numGamesPlayoffSeries: wrapFromStart([3, 5, 7, 7]),
				numPlayoffByes: wrapFromStart(4),
				numPeriods: 9,
				salaryCapType: "none",
				draftType: "mlb2022",
				numDraftRounds: 5,
				draftAges: [18, 22],
				salaryCap: 175000,
				minPayroll: 150000,
				luxuryPayroll: 200000,
				minContract: 500,
				maxContract: 30000,
				minRosterSize: 35,
				maxRosterSize: 40,
				// Arbitrary, spread over 40 plate appearances per game
				injuryRate: 0.018 / 40,
				// 200 days per season, 1 tragic death per 50 years
				tragicDeathRate: 1 / (200 * 50),
				allStarNum: 24,
				allStarType: "byConf",
				numPlayersOnCourt: 9,
				playoffsNumTeamsDiv: wrapFromStart(1),
				playIn: false,
				draftPickAutoContractPercent: 20,
				draftPickAutoContractRounds: 4,
				draftPickAutoContract: false,
				groupScheduleSeries: true,
				awards: [
					defaultAwards.mvp,
					defaultAwardsBaseball.poy,
					defaultAwardsBaseball.rpoy,
					defaultAwardsBaseball.roy,
					defaultAwardsBaseball.hankAaron,
					defaultAwards.fmvp,
					defaultAwards.all,
					defaultAwardsBaseball.def,
					defaultAwardsBaseball.allMlb,
					defaultAwards.alr,
				],
			}
		: {};

if (isSport("football")) {
	Object.assign(defaultGameAttributes, footballOverrides);
} else if (isSport("hockey")) {
	Object.assign(defaultGameAttributes, hockeyOverrides);
} else if (isSport("baseball")) {
	Object.assign(defaultGameAttributes, baseballOverrides);
}
